// =====================================================
// Virtual Tour Modal - Iframe Viewer for Matterport Tours
// =====================================================

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChatStore } from '../store/chatStore';

const VirtualTourModal: React.FC = () => {
  const {
    virtualTourModalOpen,
    virtualTourModalUrl,
    virtualTourModalTitle,
    closeVirtualTourModal
  } = useChatStore();

  // Handle keyboard events
  useEffect(() => {
    if (!virtualTourModalOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeVirtualTourModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [virtualTourModalOpen, closeVirtualTourModal]);

  // Handle click outside to close
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeVirtualTourModal();
    }
  };

  if (!virtualTourModalOpen || !virtualTourModalUrl) return null;

  // Ensure Matterport URL uses embed-friendly format
  // Add parameters to prevent navigation and hide some UI elements
  const getEmbedUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      // Remove 'back' parameter that can cause navigation issues
      urlObj.searchParams.delete('back');
      // Add embed-friendly parameters
      urlObj.searchParams.set('play', '1'); // Auto-start
      urlObj.searchParams.set('qs', '1'); // Quick start
      return urlObj.toString();
    } catch {
      return url;
    }
  };

  const embedUrl = getEmbedUrl(virtualTourModalUrl);

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 z-[100] bg-black/60 flex items-center justify-center p-2"
        onClick={handleBackdropClick}
      >
        {/* Modal Container - fits within chat window */}
        <div className="w-full h-full max-w-[95%] max-h-[95%] flex flex-col bg-white rounded-xl overflow-hidden">
          {/* Header - compact */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-white">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <svg className="w-4 h-4 text-betenbough-olive flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="text-xs font-semibold text-gray-800 truncate">
                {virtualTourModalTitle || 'Virtual Tour'}
              </span>
            </div>
            <button
              onClick={closeVirtualTourModal}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 flex-shrink-0"
              aria-label="Close virtual tour"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Iframe */}
          <div className="flex-1 bg-gray-900">
            <iframe
              src={embedUrl}
              title={virtualTourModalTitle || 'Virtual Tour'}
              className="w-full h-full"
              allowFullScreen
              allow="xr-spatial-tracking; fullscreen"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Footer - compact */}
          <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100">
            <p className="text-[10px] text-gray-500 text-center md:hidden">
              Tap to look around • Pinch to zoom • Tap hotspots to navigate
            </p>
            <p className="text-[10px] text-gray-500 text-center hidden md:block">
              Drag to look around • Scroll to zoom • Click hotspots to navigate
            </p>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VirtualTourModal;
